import { getCoreRowModel, getExpandedRowModel } from "@tanstack/react-table"
import type { ExpandedState, OnChangeFn, SortingState } from "@tanstack/react-table"
import type { FC, RefObject } from "react"
import type { CombinedError } from "urql"

import { useAdjustmentHistoryColumns } from "../../hooks/useAdjustmentHistoryColumns"

import { AdjustmentHistoryRowDetails } from "./AdjustmentHistoryRowDetails"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { AdjustmentHistoryItem } from "@/graphql/codegen/graphql"

interface AdjustmentHistoryTableProps {
  data: AdjustmentHistoryItem[]
  loading?: boolean
  error?: CombinedError | Error
  sorting: SortingState
  expanded: ExpandedState
  canMaintain: boolean
  isPrinting: boolean
  containerRef?: RefObject<HTMLDivElement>
  onScroll: () => void
  onSortingChange: OnChangeFn<SortingState>
  onExpandedChange: OnChangeFn<ExpandedState>
  onDocumentsOpen: (item: AdjustmentHistoryItem) => void
  onWorkOrderConfirm: (item: AdjustmentHistoryItem) => Promise<void>
  onWorkOrderPrint: (item: AdjustmentHistoryItem) => void
  onPalletLabelPrint: (item: AdjustmentHistoryItem, palletReferenceNumber: number) => void
  onPalletLabelPrintAll: (item: AdjustmentHistoryItem) => void
}

export const AdjustmentHistoryTable: FC<AdjustmentHistoryTableProps> = ({
  data,
  error,
  loading = false,
  sorting,
  expanded,
  canMaintain,
  isPrinting,
  containerRef,
  onScroll,
  onSortingChange,
  onExpandedChange,
  onDocumentsOpen,
  onWorkOrderConfirm,
  onWorkOrderPrint,
  onPalletLabelPrint,
  onPalletLabelPrintAll,
}) => {
  const columns = useAdjustmentHistoryColumns({
    canMaintain,
    isPrinting,
    onDocumentsOpen,
    onWorkOrderConfirm,
    onWorkOrderPrint,
    onPalletLabelPrintAll,
  })

  return (
    <AerosBaseTable
      error={error}
      features={{
        table: {
          stickyHeader: true,
        },
        tanstackOptions: {
          data,
          columns,
          state: {
            sorting,
            expanded,
            isLoading: loading,
          },
          manualSorting: true,
          enableSorting: true,
          enableMultiSort: false,
          enableSortingRemoval: false,
          getCoreRowModel: getCoreRowModel(),
          getExpandedRowModel: getExpandedRowModel(),
          onSortingChange,
          onExpandedChange,
          getRowCanExpand: () => true,
          renderExpandedContent: (row) => (
            <AdjustmentHistoryRowDetails row={row} isPrinting={isPrinting} onPalletLabelPrint={onPalletLabelPrint} />
          ),
        },
      }}
      slotProps={{
        container: { ref: containerRef, onScroll },
        bodyCellExpanded: { className: "bg-primary-100" },
        bodyCell: { className: "align-top" },
      }}
      noDataMessage='No results found according to your search criteria'
    />
  )
}
